import type {
  DepotRelances,
  DepotSav,
  FicheTicket,
  ResumeTicket,
  Ticket,
} from '@/shared/donnees/types';
import {
  peutAffecterTicket,
  peutClore,
  peutDemarrerTicket,
  peutResoudre,
  peutRouvrirTicket,
  prochaineReferenceSav,
  RAPPORT_MAX,
  statutTicketApresAffectation,
} from '@/shared/referentiel/sav';
import {
  adresseFichier,
  compteCourant,
  deposerFichier,
  journaliser,
  lireEtat,
  modifierEtat,
  nouvelId,
  retirerFichier,
  type EtatDemo,
  type TicketDemo,
} from './magasin';

function bientot<T>(valeur: T): Promise<T> {
  return new Promise((resolve) =>
    setTimeout(() => {
      resolve(structuredClone(valeur));
    }, 120)
  );
}

function refus(message: string): never {
  throw new Error(message);
}

const nomDe = (moi: ReturnType<typeof compteCourant>) => (moi ? moi.nom || moi.email : null);

function habiller(etat: EtatDemo, t: TicketDemo): Ticket {
  const chantier = etat.chantiers.find((c) => c.id === t.chantierId);
  const site = etat.sites.find((s) => s.id === chantier?.siteId);
  return {
    ...t,
    chantierReference: chantier?.reference ?? '—',
    entrepriseId: chantier?.entrepriseId ?? '',
    entrepriseNom:
      etat.entreprises.find((e) => e.id === chantier?.entrepriseId)?.raisonSociale ?? '—',
    siteNom: site?.nom ?? '—',
    commune: site?.commune ?? '',
    departement: site?.departement ?? '',
    position: site?.position ?? null,
    partenaireNom: etat.partenaires.find((p) => p.id === t.partenaireId)?.raisonSociale ?? null,
  };
}

function ficheDe(etat: EtatDemo, id: string): FicheTicket | null {
  const ticket = etat.tickets.find((t) => t.id === id);
  const chantier = ticket ? etat.chantiers.find((c) => c.id === ticket.chantierId) : undefined;
  const site = chantier ? etat.sites.find((s) => s.id === chantier.siteId) : undefined;
  if (!ticket || !chantier || !site) return null;
  return {
    ...habiller(etat, ticket),
    preuves: etat.preuvesSav
      .filter((p) => p.ticketId === id)
      .sort((a, b) => a.ajouteLe.localeCompare(b.ajouteLe)),
    site,
    contactSurPlace:
      etat.contacts.find((c) => c.entrepriseId === chantier.entrepriseId && c.surPlace) ?? null,
  };
}

function ticketDe(etat: EtatDemo, id: string): TicketDemo {
  return etat.tickets.find((t) => t.id === id) ?? refus('Ticket introuvable.');
}

/** En accès libre, un partenaire n'agit que sur ses tickets ; le bureau sur tous. */
function verifierDroit(ticket: TicketDemo, bureauSeulement = false): 'bureau' | 'partenaire' {
  const moi = compteCourant();
  if (!moi) refus('Session fermée.');
  if (moi.role === 'bureau') return 'bureau';
  if (bureauSeulement) refus('Seul le bureau peut faire cela.');
  if (moi.partenaireId === null || moi.partenaireId !== ticket.partenaireId) {
    refus('Ce ticket n’est pas confié à votre entreprise.');
  }
  return 'partenaire';
}

function seulementLeBureau(): void {
  const moi = compteCourant();
  if (!moi) refus('Session fermée.');
  if (moi.role !== 'bureau') refus('Seul le bureau peut faire cela.');
}

function verifierSaisie(etat: EtatDemo, s: { chantierId: string; objet: string }): void {
  if (s.objet.trim() === '') refus('Donnez un objet au ticket.');
  if (!etat.chantiers.some((c) => c.id === s.chantierId)) refus('Ce chantier n’existe plus.');
}

/** Modifier un ticket et le noter au journal, comme le ferait la base. */
function changer(id: string, bureauSeulement: boolean, agir: (t: TicketDemo) => void): void {
  modifierEtat((e) => {
    const ticket = ticketDe(e, id);
    verifierDroit(ticket, bureauSeulement);
    const avant = { ...ticket };
    agir(ticket);
    journaliser(e, 'modification', 'tickets_sav', id, ticket.reference, avant, { ...ticket });
  });
}

export const sav: DepotSav = {
  lister() {
    const etat = lireEtat();
    const resumes: ResumeTicket[] = etat.tickets
      .map((t) => ({
        ...habiller(etat, t),
        nbPhotos: etat.preuvesSav.filter((p) => p.ticketId === t.id && p.type === 'photo').length,
      }))
      .sort((a, b) => b.ouvertLe.localeCompare(a.ouvertLe));
    return bientot(resumes);
  },

  lire(id) {
    return bientot(ficheDe(lireEtat(), id));
  },

  fiches(ids) {
    const etat = lireEtat();
    return bientot([...new Set(ids)].map((id) => ficheDe(etat, id)).filter((f) => f !== null));
  },

  creer(saisie) {
    try {
      seulementLeBureau();
      verifierSaisie(lireEtat(), saisie);
    } catch (e) {
      return Promise.reject(e instanceof Error ? e : new Error(String(e)));
    }
    const etat = lireEtat();
    const ticket: TicketDemo = {
      id: nouvelId('sav'),
      reference: prochaineReferenceSav(
        etat.tickets.map((t) => t.reference),
        new Date().getFullYear()
      ),
      chantierId: saisie.chantierId,
      objet: saisie.objet.trim(),
      description: saisie.description.trim(),
      priorite: saisie.priorite,
      statut: 'a-planifier',
      partenaireId: null,
      debutPrevu: null,
      finPrevue: null,
      ouvertLe: new Date().toISOString(),
      ouvertPar: nomDe(compteCourant()),
      demarreLe: null,
      resoluLe: null,
      resoluPar: null,
      rapport: '',
      closLe: null,
      closPar: null,
      motifReouverture: null,
    };
    modifierEtat((e) => {
      e.tickets.push(ticket);
      journaliser(e, 'creation', 'tickets_sav', ticket.id, ticket.reference);
    });
    return bientot(habiller(lireEtat(), ticket));
  },

  signaler(s) {
    const moi = compteCourant();
    const partenaireId = moi?.partenaireId ?? null;
    if (!partenaireId)
      return Promise.reject(new Error('Seul un partenaire rattaché signale un SAV.'));
    if (!s.piece.trim()) return Promise.reject(new Error('Indiquez la pièce défaillante.'));
    const etat = lireEtat();
    const sien = etat.etapes.some(
      (x) => x.chantierId === s.chantierId && x.partenaireId === partenaireId
    );
    if (!sien) return Promise.reject(new Error('Ce chantier n’est pas l’un des vôtres.'));
    const ticket: TicketDemo = {
      id: nouvelId('sav'),
      reference: prochaineReferenceSav(
        etat.tickets.map((t) => t.reference),
        new Date().getFullYear()
      ),
      chantierId: s.chantierId,
      objet: `${s.systeme === 'solaire' ? 'Panneaux solaires' : 'Système de séchage'} — ${s.piece.trim()}`,
      description: s.commentaire.trim(),
      priorite: s.arret === 'oui' ? 'urgente' : 'normale',
      systeme: s.systeme,
      piece: s.piece.trim(),
      marqueModele: s.marqueModele.trim() || null,
      numeroSerie: s.numeroSerie.trim() || null,
      etatPiece: s.etatPiece,
      constateLe: s.constateLe,
      arret: s.arret,
      statut: 'a-planifier',
      partenaireId,
      debutPrevu: null,
      finPrevue: null,
      ouvertLe: new Date().toISOString(),
      ouvertPar: nomDe(moi),
      demarreLe: null,
      resoluLe: null,
      resoluPar: null,
      rapport: '',
      closLe: null,
      closPar: null,
      motifReouverture: null,
    };
    modifierEtat((e) => {
      e.tickets.push(ticket);
      journaliser(e, 'creation', 'tickets_sav', ticket.id, ticket.reference);
    });
    return bientot(ticket.id);
  },

  modifier(id, saisie) {
    modifierEtat((e) => {
      seulementLeBureau();
      verifierSaisie(e, saisie);
      const ticket = ticketDe(e, id);
      const avant = { ...ticket };
      ticket.chantierId = saisie.chantierId;
      ticket.objet = saisie.objet.trim();
      ticket.description = saisie.description.trim();
      ticket.priorite = saisie.priorite;
      journaliser(e, 'modification', 'tickets_sav', id, ticket.reference, avant, { ...ticket });
    });
    return bientot(undefined);
  },

  supprimer(id) {
    modifierEtat((e) => {
      seulementLeBureau();
      const ticket = ticketDe(e, id);
      e.preuvesSav
        .filter((p) => p.ticketId === id)
        .forEach((p) => {
          retirerFichier(p.chemin);
        });
      e.preuvesSav = e.preuvesSav.filter((p) => p.ticketId !== id);
      e.tickets = e.tickets.filter((t) => t.id !== id);
      journaliser(e, 'suppression', 'tickets_sav', id, ticket.reference);
    });
    return bientot(undefined);
  },

  affecter(id, affectation) {
    changer(id, true, (t) => {
      const verdict = peutAffecterTicket(t);
      if (!verdict.ok) refus(verdict.raison);
      if (
        affectation.debutPrevu &&
        affectation.finPrevue &&
        affectation.finPrevue < affectation.debutPrevu
      ) {
        refus('La fin prévue précède le début.');
      }
      t.partenaireId = affectation.partenaireId;
      t.debutPrevu = affectation.debutPrevu;
      t.finPrevue = affectation.finPrevue;
      t.statut = statutTicketApresAffectation(t.statut, affectation);
    });
    return bientot(undefined);
  },

  demarrer(id) {
    changer(id, false, (t) => {
      const verdict = peutDemarrerTicket(t);
      if (!verdict.ok) refus(verdict.raison);
      t.statut = 'en-cours';
      t.demarreLe = new Date().toISOString();
    });
    return bientot(undefined);
  },

  async ajouterPreuve(id, fichier, type) {
    const etat = lireEtat();
    const ticket = ticketDe(etat, id);
    verifierDroit(ticket);
    if (ticket.statut === 'resolu' || ticket.statut === 'clos') {
      refus('Ce ticket est résolu : rouvrez-le pour ajouter une preuve.');
    }
    const preuveId = nouvelId('ps');
    const chemin = `sav/${id}/${preuveId}-${fichier.name}`;
    await deposerFichier(chemin, fichier);
    modifierEtat((e) => {
      e.preuvesSav.push({
        id: preuveId,
        ticketId: id,
        type,
        nom: fichier.name,
        taille: fichier.size,
        typeMime: fichier.type || 'application/octet-stream',
        chemin,
        ajouteLe: new Date().toISOString(),
        ajoutePar: compteCourant()?.id ?? null,
      });
      journaliser(e, 'creation', 'preuves_sav', preuveId, fichier.name);
    });
    return bientot(undefined);
  },

  supprimerPreuve(preuveId) {
    modifierEtat((e) => {
      const preuve =
        e.preuvesSav.find((p) => p.id === preuveId) ?? refus('Cette preuve n’existe plus.');
      const ticket = ticketDe(e, preuve.ticketId);
      verifierDroit(ticket);
      if (ticket.statut === 'resolu' || ticket.statut === 'clos') {
        refus('Ce ticket est résolu : ses preuves ne se retirent plus.');
      }
      retirerFichier(preuve.chemin);
      e.preuvesSav = e.preuvesSav.filter((p) => p.id !== preuveId);
      journaliser(e, 'suppression', 'preuves_sav', preuveId, preuve.nom);
    });
    return bientot(undefined);
  },

  adressePreuve(chemin) {
    const nom = lireEtat().preuvesSav.find((p) => p.chemin === chemin)?.nom ?? chemin;
    return adresseFichier(chemin, nom);
  },

  resoudre(id, rapport) {
    modifierEtat((e) => {
      const ticket = ticketDe(e, id);
      const qui = verifierDroit(ticket);
      const photos = e.preuvesSav.filter((p) => p.ticketId === id && p.type === 'photo').length;
      const verdict = peutResoudre(ticket, rapport, photos, qui === 'bureau');
      if (!verdict.ok) refus(verdict.raison);
      const avant = { ...ticket };
      ticket.statut = 'resolu';
      ticket.demarreLe ??= new Date().toISOString();
      ticket.resoluLe = new Date().toISOString();
      ticket.resoluPar = nomDe(compteCourant());
      ticket.rapport = rapport.trim().slice(0, RAPPORT_MAX);
      ticket.motifReouverture = null;
      journaliser(e, 'modification', 'tickets_sav', id, ticket.reference, avant, { ...ticket });
    });
    return bientot(undefined);
  },

  clore(id) {
    changer(id, true, (t) => {
      const verdict = peutClore(t);
      if (!verdict.ok) refus(verdict.raison);
      t.statut = 'clos';
      t.closLe = new Date().toISOString();
      t.closPar = nomDe(compteCourant());
    });
    return bientot(undefined);
  },

  rouvrir(id, motif) {
    if (motif.trim() === '') {
      return Promise.reject(new Error('Dites pourquoi le ticket est rouvert.'));
    }
    changer(id, true, (t) => {
      const verdict = peutRouvrirTicket(t);
      if (!verdict.ok) refus(verdict.raison);
      t.statut = 'rouvert';
      t.motifReouverture = motif.trim();
      t.resoluLe = null;
      t.resoluPar = null;
      t.closLe = null;
      t.closPar = null;
    });
    return bientot(undefined);
  },

  historique(id) {
    const etat = lireEtat();
    const preuves = new Set(etat.preuvesSav.filter((p) => p.ticketId === id).map((p) => p.id));
    return bientot(
      etat.journal
        .filter(
          (j) =>
            (j.objetType === 'tickets_sav' && j.objetId === id) ||
            (j.objetType === 'preuves_sav' && preuves.has(j.objetId))
        )
        .slice(0, 100)
        .map((j) => ({
          id: j.id,
          horodatage: j.horodatage,
          acteurNom: j.acteurNom,
          action: j.action,
          objetType: j.objetType,
          libelle: j.libelle,
          changements: j.changements,
        }))
    );
  },
};

export const relances: DepotRelances = {
  lister() {
    return bientot([...lireEtat().relances].sort((a, b) => b.envoyeeLe.localeCompare(a.envoyeeLe)));
  },
  noter(partenaireId, sujet) {
    try {
      seulementLeBureau();
    } catch (e) {
      return Promise.reject(e instanceof Error ? e : new Error(String(e)));
    }
    modifierEtat((e) => {
      e.relances.push({
        id: nouvelId('rel'),
        partenaireId,
        sujet,
        envoyeeLe: new Date().toISOString(),
        envoyeePar: nomDe(compteCourant()),
      });
    });
    return bientot(undefined);
  },
};
